/**
 * The timezones a tenant may be set to.
 *
 * MIRROR of `SUPPORTED_TIMEZONES` in `fieldiq-api/src/lib/format-date.ts`. The
 * API is the source of truth; this file exists so the browser stops carrying
 * its own answer. If you are changing the list, change it there first, then
 * port it here — `src/constants/timezones.test.ts` fails if this drifts out of
 * subset with the API's.
 *
 * MEMBERSHIP, NOT VALIDITY — the API's own framing, worth repeating because it
 * is the trap. `IANAZone.isValidZone('MST')` returns TRUE: it is a real tzdata
 * entry, and it is a FIXED-OFFSET zone that never observes DST. A tenant saved
 * as 'MST' would validate cleanly and be an hour wrong for half of every year.
 * Same for 'US/Mountain', a legacy link. The question is never whether a zone
 * exists; it is whether it is one this product supports.
 *
 * WHY THIS FILE EXISTS. One column — `companies.time_zone` — was edited from
 * two screens against THREE different option lists, none of which agreed with
 * the API:
 *
 *   RegionalTaxPage.tsx            10 options, inline <option> tags. Missing
 *                                  St_Johns, Regina and Phoenix, so
 *                                  Newfoundland and both no-DST zones were
 *                                  unreachable.
 *   NotificationPreferencesScreen  23 options, TEN of them not in the
 *                                  allowlist. PATCH /company/alert-settings
 *                                  validates and returns 400, so choosing
 *                                  Honolulu or London failed the save with no
 *                                  hint that the LIST was wrong rather than
 *                                  the app.
 *   PayPeriodSettingsScreen        12 options writing to a column dropped in
 *                                  API migration 0325 — deleted in the same PR
 *                                  as this file was added.
 *
 * REGINA AND PHOENIX ARE THE POINT, not padding. Both sit at a fixed offset all
 * year. Saskatchewan does not observe DST, so a SK tenant forced onto
 * America/Edmonton or America/Winnipeg is an hour out for half the year — and
 * SK is a working oilfield province.
 */

export interface TimezoneOption {
  /** IANA zone id. Must be a member of the API's SUPPORTED_TIMEZONES. */
  value: string;
  /** What the operator reads. Names the REGION, not a representative city. */
  label: string;
}

/**
 * The 13 supported zones, ordered as the API lists them: Canada east to west,
 * then the United States east to west.
 *
 * The labels deliberately do not follow the API's inline comments verbatim.
 * "Mountain (AB)" is right where the old PayPeriodSettings list said
 * "Mountain (AB/SK)" — that label is why Saskatchewan looked covered when it
 * was not, and it is the reason this list carries Regina separately.
 */
export const TIMEZONE_OPTIONS: readonly TimezoneOption[] = [
  // Canada, east to west
  { value: 'America/St_Johns', label: 'Newfoundland — NST/NDT' },
  { value: 'America/Halifax', label: 'Atlantic (NS/NB/PE) — AST/ADT' },
  { value: 'America/Toronto', label: 'Eastern (ON/QC) — EST/EDT' },
  { value: 'America/Winnipeg', label: 'Central (MB) — CST/CDT' },
  { value: 'America/Regina', label: 'Saskatchewan — CST, no DST' },
  { value: 'America/Edmonton', label: 'Mountain (AB) — MST/MDT' },
  { value: 'America/Vancouver', label: 'Pacific (BC) — PST/PDT' },
  // United States, east to west
  { value: 'America/New_York', label: 'US Eastern — EST/EDT' },
  { value: 'America/Chicago', label: 'US Central — CST/CDT' },
  { value: 'America/Denver', label: 'US Mountain — MST/MDT' },
  { value: 'America/Phoenix', label: 'Arizona — MST, no DST' },
  { value: 'America/Los_Angeles', label: 'US Pacific — PST/PDT' },
  { value: 'America/Anchorage', label: 'Alaska — AKST/AKDT' },
] as const;

/** Just the zone ids, in the same order. */
export const TIMEZONE_VALUES: readonly string[] = TIMEZONE_OPTIONS.map((t) => t.value);

const SUPPORTED = new Set<string>(TIMEZONE_VALUES);

/** Is this one of the zones a tenant may be set to? Membership, not validity. */
export function isSupportedTimezone(value: unknown): value is string {
  return typeof value === 'string' && SUPPORTED.has(value);
}

/**
 * The zone to SUGGEST when an admin changes the company's country.
 *
 * A suggestion, never a value: Regional & Tax shows it beside the Time Zone
 * field with a button to take it, and the admin's own pick is what is saved.
 * Changing the country never rewrites a zone that is already set.
 *
 * There used to be a `DEFAULT_TIMEZONE` here ('America/Edmonton') "for a form
 * with none stored". It had no caller and was removed with the rule that a
 * tenant's zone comes from `companies.time_zone` and nowhere else
 * (lib/tenant-zone.ts). This file is the one place the lint
 * (no-hardcoded-time-zone) lets a zone be spelled.
 */
export const COUNTRY_SUGGESTED_TIMEZONE: Readonly<Record<'CA' | 'US', string>> = {
  CA: 'America/Edmonton',
  US: 'America/Chicago',
};

/**
 * Label for a stored zone, falling back to the raw id.
 *
 * A stored value outside the list must still RENDER. Nothing in the database
 * constrains `companies.time_zone` to this vocabulary — the allowlist is
 * enforced at the write endpoints, not by a CHECK — so a row set before the
 * allowlist existed, or by a direct SQL fix, has to survive being displayed.
 * Blanking it would hide the very drift this file exists to surface.
 */
export function timezoneLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return TIMEZONE_OPTIONS.find((t) => t.value === value)?.label ?? value;
}
